import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { LIGHTS, lightOf, roomIndex } from './rooms.js';
import { fixtureGrid } from './materials.js';

/**
 * The fittings that light each room, drawn, and a handful of real lights that
 * follow the camera round them.
 *
 * Every room's light is already in its surfaces (see `materials.js`); what is
 * drawn here is the fitting itself — a glowing panel in a back-of-house
 * ceiling, a drum pendant over the marble, a wall pack on the dock at night —
 * and a small pool of point lights, handed to whichever fittings are nearest
 * the camera, so people and loose things standing under one are lit by it.
 * The pool never changes size: a changing number of lights recompiles every
 * shader in the building.
 */

const FT = 0.3048;
/** Two: measured in `perf.mjs`, each light in the pool is a loop on every lit pixel. */
export const POOL = 2;

/** Where every fitting is, what it is, and what it gives off. Plain data. */
export function fixturesOf(plan) {
  const out = [];
  for (const room of plan.rooms) {
    const light = lightOf(room);
    if (light === 'outdoors') continue;
    const spec = LIGHTS[light];
    const grid = fixtureGrid(room, spec.spacing);
    const ceiling = ((room.base ?? 0) + room.ceiling) * FT;
    const [x0, z0, x1, z1] = room.rect;
    const alongX = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    for (const p of grid.points) {
      out.push({ room: room.id, kind: spec.fixture, light, x: p.x, z: p.z, y: ceiling, alongX, colour: spec.colour, outdoor: false });
    }
  }
  // Wall packs: on each outside face of a wall that has a room on its other
  // side, every forty feet. They are what lights the dock and the lot at night.
  const roomAt = roomIndex(plan.rooms);
  for (const wall of plan.walls) {
    const [ax, az] = wall.from;
    const [bx, bz] = wall.to;
    const length = Math.hypot(bx - ax, bz - az);
    const count = Math.floor(length / 40);
    if (!count) continue;
    const nx = -(bz - az) / length;
    const nz = (bx - ax) / length;
    for (let i = 0; i < count; i += 1) {
      const t = (i + 0.5) / count;
      const x = (ax + (bx - ax) * t) * FT;
      const z = (az + (bz - az) * t) * FT;
      for (const side of [1, -1]) {
        const out1 = roomAt(x + nx * side * 0.6, z + nz * side * 0.6);
        const in1 = roomAt(x - nx * side * 0.6, z - nz * side * 0.6);
        if (!out1 || out1.ceiling || !(wall.height >= 7)) continue;
        if (in1 && !in1.ceiling) continue;
        const y = Math.min(wall.height - 1, 11) * FT;
        out.push({ room: out1.id, kind: 'wallpack', light: 'outdoors', x: x + nx * side * 0.12, z: z + nz * side * 0.12, y, facing: [nx * side, nz * side], colour: 0xffc890, outdoor: true });
      }
    }
  }
  return out;
}

/** Instanced meshes for every fitting, a few draw calls for the whole building. */
export function createFixtures(plan, { environment = null } = {}) {
  const group = new THREE.Group();
  group.name = 'look-fixtures';
  const all = fixturesOf(plan);
  const disposables = [];
  const keep = (x) => (disposables.push(x), x);
  const dummy = new THREE.Object3D();

  function instanced(list, geometry, material, place) {
    if (!list.length) return null;
    const mesh = new THREE.InstancedMesh(keep(geometry), keep(material), list.length);
    list.forEach((f, i) => {
      dummy.position.set(0, 0, 0);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      place(f, dummy);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    group.add(mesh);
    return mesh;
  }

  const glow = (colour, strength = 1) =>
    new THREE.MeshStandardMaterial({ color: 0x222222, emissive: colour, emissiveIntensity: strength, roughness: 0.6 });

  // Two-by-four lay-in troffers, a glowing lens in a white frame.
  const troffers = all.filter((f) => f.kind === 'troffer');
  instanced(troffers, new THREE.BoxGeometry(4 * FT, 0.04, 2 * FT), glow(0xf2f6ff, 2.2), (f, d) => {
    d.position.set(f.x, f.y - 0.02, f.z);
    if (!f.alongX) d.rotation.y = Math.PI / 2;
  });
  instanced(troffers, new THREE.BoxGeometry(4.3 * FT, 0.03, 2.3 * FT), new THREE.MeshStandardMaterial({ color: 0xe8e8e4, roughness: 0.5 }), (f, d) => {
    d.position.set(f.x, f.y - 0.012, f.z);
    if (!f.alongX) d.rotation.y = Math.PI / 2;
  });

  // Recessed cans in the office.
  instanced(all.filter((f) => f.kind === 'can'), new THREE.CylinderGeometry(0.5 * FT, 0.5 * FT, 0.03, 16), glow(0xffe8cc, 2.4), (f, d) => {
    d.position.set(f.x, f.y - 0.015, f.z);
  });

  // Drum pendants over the lobby: a bronze drum on a rod, lit from beneath.
  const pendants = all.filter((f) => f.kind === 'pendant');
  const drop = 7 * FT;
  instanced(pendants, new THREE.CylinderGeometry(1.6 * FT, 1.6 * FT, 1.4 * FT, 24, 1, true), new THREE.MeshStandardMaterial({ color: 0x6a4a2a, metalness: 0.7, roughness: 0.35, side: THREE.DoubleSide, emissive: 0x3a2410, emissiveIntensity: 0.6, envMap: environment }), (f, d) => {
    d.position.set(f.x, f.y - drop, f.z);
  });
  instanced(pendants, new THREE.CylinderGeometry(1.55 * FT, 1.55 * FT, 0.02, 24), glow(0xfff0dc, 3), (f, d) => {
    d.position.set(f.x, f.y - drop - 0.7 * FT, f.z);
  });
  instanced(pendants, new THREE.CylinderGeometry(0.02, 0.02, drop, 6), new THREE.MeshStandardMaterial({ color: 0x3a2a1a, metalness: 0.8, roughness: 0.3 }), (f, d) => {
    d.position.set(f.x, f.y - drop / 2 + 0.35 * FT, f.z);
  });

  /*
   * Crystal chandeliers in the ballroom: a brass stem and three tiers of arms,
   * each arm ending in a candle bulb, hung with drops. Two instanced meshes for
   * all of them — the metal, and everything that glows.
   */
  const chandeliers = all.filter((f) => f.kind === 'chandelier');
  const chandelierGlow = glow(0xffd49a, 2.6);
  let chandelierLevel = 1;
  if (chandeliers.length) {
    const { metal, glowing } = chandelierParts();
    instanced(chandeliers, metal, new THREE.MeshStandardMaterial({ color: 0xc9a45a, metalness: 0.9, roughness: 0.25, emissive: 0x3a2808, emissiveIntensity: 0.5, envMap: environment }), (f, d) => {
      d.position.set(f.x, f.y, f.z);
    });
    instanced(chandeliers, glowing, chandelierGlow, (f, d) => {
      d.position.set(f.x, f.y, f.z);
    });
  }

  // Wall packs: a dark housing with an amber lens, lit at dusk.
  const packs = all.filter((f) => f.kind === 'wallpack');
  instanced(packs, new THREE.BoxGeometry(1.2 * FT, 0.9 * FT, 0.7 * FT), new THREE.MeshStandardMaterial({ color: 0x3a3c40, roughness: 0.6, metalness: 0.4 }), (f, d) => {
    d.position.set(f.x, f.y, f.z);
    d.rotation.y = Math.atan2(f.facing[0], f.facing[1]);
  });
  const packGlow = glow(0xffc890, 0);
  instanced(packs, new THREE.BoxGeometry(1 * FT, 0.4 * FT, 0.05), packGlow, (f, d) => {
    d.position.set(f.x + f.facing[0] * 0.36 * FT, f.y - 0.18 * FT, f.z + f.facing[1] * 0.36 * FT);
    d.rotation.y = Math.atan2(f.facing[0], f.facing[1]);
  });

  // The pool of real lights.
  const pool = [];
  for (let i = 0; i < POOL; i += 1) {
    const light = new THREE.PointLight(0xffffff, 0, 12, 2);
    light.name = `look-pool-${i}`;
    light.castShadow = false;
    pool.push(light);
    group.add(light);
  }

  let every = 0;
  return {
    object3D: group,
    fixtures: all,
    /** How bright the chandeliers burn: 2.6 working, lower for an event. */
    setChandeliers(intensity) {
      chandelierGlow.emissiveIntensity = intensity;
      chandelierLevel = intensity / 2.6;
    },
    /** The pool follows the camera; the outside lights come on with the dark. */
    update({ camera, dark }) {
      packGlow.emissiveIntensity = 2.5 * dark;
      every -= 1;
      if (every > 0) return;
      every = 6;
      const lit = all
        .filter((f) => !f.outdoor || dark > 0.25)
        .map((f) => ({ f, d: (f.x - camera.x) ** 2 + (f.z - camera.z) ** 2 + (f.y - camera.y) ** 2 }))
        .sort((a, b) => a.d - b.d)
        .slice(0, POOL);
      pool.forEach((light, i) => {
        const pick = lit[i];
        if (!pick) {
          light.intensity = 0;
          return;
        }
        const f = pick.f;
        light.color.set(f.colour);
        light.position.set(
          f.x + (f.facing ? f.facing[0] * 0.8 : 0),
          f.y - (f.kind === 'pendant' ? 7.8 * FT : f.kind === 'chandelier' ? 8 * FT : 0.3),
          f.z + (f.facing ? f.facing[1] * 0.8 : 0)
        );
        light.distance = f.outdoor ? 16 : f.kind === 'chandelier' || f.kind === 'pendant' ? 14 : 8;
        light.intensity = f.outdoor ? 26 * dark : f.kind === 'chandelier' ? 30 * chandelierLevel : f.kind === 'pendant' ? 26 : 9;
      });
    },
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}

/** One chandelier, hanging down from y = 0, in two merged geometries. */
function chandelierParts() {
  const metal = [];
  const glowing = [];
  const at = (g, x, y, z) => g.translate(x, y, z);
  // The stem and the canopy.
  metal.push(at(new THREE.CylinderGeometry(0.03, 0.03, 3 * FT, 6), 0, -1.5 * FT, 0));
  metal.push(at(new THREE.CylinderGeometry(0.6 * FT, 0.3 * FT, 0.3 * FT, 12), 0, -0.15 * FT, 0));
  metal.push(at(new THREE.SphereGeometry(0.5 * FT, 10, 8), 0, -6.2 * FT, 0));
  const tiers = [
    { y: -3.6 * FT, r: 1.4 * FT, arms: 6 },
    { y: -4.8 * FT, r: 2.4 * FT, arms: 10 },
    { y: -5.8 * FT, r: 1.8 * FT, arms: 8 },
  ];
  for (const tier of tiers) {
    const ring = new THREE.TorusGeometry(tier.r, 0.02, 6, 32);
    ring.rotateX(Math.PI / 2);
    metal.push(at(ring, 0, tier.y, 0));
    for (let i = 0; i < tier.arms; i += 1) {
      const a = (i / tier.arms) * Math.PI * 2;
      const x = Math.cos(a) * tier.r;
      const z = Math.sin(a) * tier.r;
      // A candle and its bulb.
      metal.push(at(new THREE.CylinderGeometry(0.035, 0.035, 0.18, 6), x, tier.y + 0.09, z));
      glowing.push(at(new THREE.SphereGeometry(0.05, 8, 6), x, tier.y + 0.22, z));
      // Drops of crystal hanging off the ring, between the arms.
      const b = a + Math.PI / tier.arms;
      const drop = new THREE.OctahedronGeometry(0.045, 0);
      drop.scale(1, 2.2, 1);
      glowing.push(at(drop, Math.cos(b) * tier.r, tier.y - 0.12, Math.sin(b) * tier.r));
    }
  }
  // A cascade of crystal down the middle.
  for (let i = 0; i < 14; i += 1) {
    const a = i * 2.4;
    const r = 0.25 + (i % 3) * 0.12;
    const drop = new THREE.OctahedronGeometry(0.05, 0);
    drop.scale(1, 2.4, 1);
    glowing.push(at(drop, Math.cos(a) * r, -3.9 * FT - i * 0.1, Math.sin(a) * r));
  }
  const clean = (list) => list.map((g) => (g.index ? g.toNonIndexed() : g)).map((g) => {
    for (const name of Object.keys(g.attributes)) if (!['position', 'normal', 'uv'].includes(name)) g.deleteAttribute(name);
    return g;
  });
  return { metal: mergeGeometries(clean(metal)), glowing: mergeGeometries(clean(glowing)) };
}
